import { isNumber } from 'lodash';

import { OddsTypes } from 'constants/app';
import { OddsType } from 'types';

const fixFloatError = (n: number) => parseFloat(n.toPrecision(12));

const toMYOdds = (odds: number) => {
  if (odds <= 2.0) {
    return fixFloatError(odds - 1);
  }
  return parseFloat(fixFloatError(-1 / (odds - 1)).toFixed(3));
};

const toINDOdds = (odds: number) => {
  if (odds === 0) {
    throw new Error('Outside valid range.');
  }

  if (odds >= 2) {
    return fixFloatError(odds - 1);
  }
  return parseFloat(fixFloatError(-1 / (odds - 1)).toFixed(2));
};

const toUSOdds = (odds: number) => {
  if (odds < 2) {
    return fixFloatError(-100 / (odds - 1)).toFixed(0);
  } else {
    return `+${Math.round(fixFloatError((odds - 1.0) * 100))}`;
  }
};

export const convertToUSOddsWithPrecision = (decimalOdds: number | string) => {
  const odds = decimalOdds && isNumber(+decimalOdds) ? +decimalOdds : decimalOdds;
  const oddsPrecision = odds.toString().split('.')[1]?.length || 0;
  const precision = oddsPrecision > 2 ? oddsPrecision : 0;

  if (isNumber(odds)) {
    if (+odds < 2) {
      return parseFloat(fixFloatError(-100 / (+odds - 1)).toFixed(precision));
    } else {
      return `+${parseFloat(fixFloatError((+odds - 1.0) * 100).toFixed(precision))}`;
    }
  } else {
    return decimalOdds;
  }
};

export const fromUSOdds = (american: string, noRounding?: boolean) => {
  const americanOdds = parseFloat(american);

  if (isNaN(americanOdds)) {
    return '';
  }

  if (americanOdds >= 0) {
    if (americanOdds < 100) {
      return 2;
    }
    return parseFloat(
      noRounding ? fixFloatError(americanOdds / 100 + 1).toString() : fixFloatError(americanOdds / 100 + 1).toFixed(2)
    );
  } else {
    if (americanOdds > -100) {
      return 2;
    }
    return parseFloat(
      noRounding
        ? fixFloatError(100 / Math.abs(americanOdds) + 1).toString()
        : fixFloatError(100 / Math.abs(americanOdds) + 1).toFixed(2)
    );
  }
};

const convertOdds = (decimalOdds: number | string, type: OddsType, isAsianView?: boolean) => {
  const odds = decimalOdds && isNumber(+decimalOdds) ? +decimalOdds : decimalOdds;
  if (isNumber(odds)) {
    switch (type) {
      case OddsTypes.DEC:
        const decOdds = floatFormat(odds);
        return isAsianView && decOdds < 1000 ? decOdds.toFixed(2) : decOdds;
      case OddsTypes.MY:
        return toMYOdds(odds);
      case OddsTypes.HK:
        return floatFormat(fixFloatError(odds - 1));
      case OddsTypes.IND:
        return toINDOdds(odds);
      case OddsTypes.US:
        return toUSOdds(odds);
    }
  }
  return decimalOdds;
};

export const floatFormat = (odds: number | string, precision = 2) =>
  parseFloat(parseFloat((odds || '').toString()).toFixed(precision));

export default convertOdds;
